import React from 'react';
import {
    FormControl,
    InputLabel,
    MenuItem,
    TextField,
    Select,
    CircularProgress,
    Typography,
    Grid,
    FormControlLabel,
    Radio,
    FormLabel
} from '@mui/material';
import {
    FormPageContainer,
    FormHeader,
    FormSubHeader,
    StyledForm,
    SubmitButton,
    ButtonGroupContainer,
    SectionContainer, // Reusing Paper container
    AddButton,        // Reusing Add button style
    RemoveButton,     // Reusing Remove button style
    EstateSectionHeader,
    EstateControlsContainer,
    EstateButtonsGroup,
    EstateTypeButton,
    EstateItemsGrid,
    EstateItemPaper,
    EstateItemHeader,
    BeneficiarySelectionContainer,
    RemainderBeneficiaryPaper,
    NoItemsTypography,
    PercentageHelperBox,
    RadioGroupContainer, // Need RadioGroup styles
    StyledRadioGroup, // Need RadioGroup styles
    BackButton
} from './EstateDisposalForm.styles';

export default function EstateDisposalForm({
    formData,
    handleSubmit,
    handleBack,
    isSubmitting,
    addEstateItem,
    updateEstateItem,
    removeEstateItem,
    addRemainderBeneficiary,
    updateRemainderBeneficiary,
    removeRemainderBeneficiary,
    handleBeneficiarySelect,
    getPotentialBeneficiaries,
}) {
    // Specific handler for nested estate item fields
    const handleItemChange = (itemId, field, value) => {
        updateEstateItem(itemId, field, value);
    };

    // Specific handler for nested remainder beneficiary fields
    const handleRemainderChange = (beneficiaryId, field, value) => {
        updateRemainderBeneficiary(beneficiaryId, field, value);
    };

    // Handler for radio buttons within estate items
    const handleItemRadioChange = (itemId, field, value) => {
        updateEstateItem(itemId, field, value === 'true');
    };

    const totalRemainderPercentage = formData.remainderBeneficiaries.reduce(
        (sum, ben) => sum + (parseInt(ben.percentage, 10) || 0),
        0
    );
    const percentageError = totalRemainderPercentage !== 100;

    return (
        <FormPageContainer>
            <FormHeader variant="h4" component="h1">Will & Testament Creator</FormHeader>
            <FormSubHeader variant="h5" component="h2">Step 5: Disposal of Estate</FormSubHeader>
            <StyledForm component="form" onSubmit={handleSubmit} noValidate>
                <SectionContainer variant="outlined">
                    <EstateSectionHeader variant="h6">Special Bequests</EstateSectionHeader>

                    <EstateControlsContainer>
                        <FormLabel component="legend">Add Estate Item:</FormLabel>
                        <EstateButtonsGroup>
                            <EstateTypeButton variant="outlined" size="small" onClick={() => addEstateItem("immovable")}>Immovable Property</EstateTypeButton>
                            <EstateTypeButton variant="outlined" size="small" onClick={() => addEstateItem("movable")}>Movable Property</EstateTypeButton>
                            <EstateTypeButton variant="outlined" size="small" onClick={() => addEstateItem("cash")}>Cash Benefit</EstateTypeButton>
                            <EstateTypeButton variant="outlined" size="small" onClick={() => addEstateItem("business")}>Business Ownership</EstateTypeButton>
                            <EstateTypeButton variant="outlined" size="small" onClick={() => addEstateItem("trust")}>Trust</EstateTypeButton>
                            <EstateTypeButton variant="outlined" size="small" onClick={() => addEstateItem("digital")}>Digital Asset</EstateTypeButton>
                            <EstateTypeButton variant="outlined" size="small" onClick={() => addEstateItem("personal")}>Personal Bequest</EstateTypeButton>
                            <EstateTypeButton variant="outlined" size="small" onClick={() => addEstateItem("policy")}>Policy/Investment</EstateTypeButton>
                        </EstateButtonsGroup>
                    </EstateControlsContainer>

                    {formData.estateItems.length === 0 && (
                        <NoItemsTypography>
                            No specific estate items added yet. Use the buttons above to add items you wish to bequeath specifically.
                        </NoItemsTypography>
                    )}

                    {formData.estateItems.length > 0 && (
                        <EstateItemsGrid container spacing={3}>
                            {formData.estateItems.map((item) => (
                                <Grid item xs={12} md={6} lg={4} key={item.id}>
                                    <EstateItemPaper variant="outlined">
                                        <EstateItemHeader variant="subtitle1">
                                            {(() => {
                                                switch (item.type) {
                                                    case "immovable": return "Immovable Property";
                                                    case "movable": return "Movable Property";
                                                    case "cash": return "Cash Benefit";
                                                    case "business": return "Business Ownership";
                                                    case "trust": return "Trust";
                                                    case "digital": return "Digital Asset";
                                                    case "personal": return "Personal Bequest";
                                                    case "policy": return "Policy/Investment";
                                                    default: return "Estate Item";
                                                }
                                            })()}
                                        </EstateItemHeader>

                                        {/* === Immovable Property === */}
                                        {item.type === "immovable" && (
                                            <>
                                                <TextField label="Property Address" value={item.address} onChange={(e) => handleItemChange(item.id, "address", e.target.value)} required multiline rows={2} fullWidth />
                                                <BeneficiarySelectionContainer>
                                                    <FormControl fullWidth>
                                                        <InputLabel id={`immovable-beneficiary-select-${item.id}`}>Beneficiary</InputLabel>
                                                        <Select labelId={`immovable-beneficiary-select-${item.id}`} value="" label="Beneficiary" onChange={(e) => handleBeneficiarySelect(item.id, "estateItem", e.target.value, "beneficiaries")}>
                                                            <MenuItem value=""><em>-- Select Existing --</em></MenuItem>
                                                            {getPotentialBeneficiaries().map(ben => (<MenuItem key={ben.id} value={ben.id}>{ben.name} ({ben.relationship})</MenuItem>))}
                                                            <MenuItem value="other"><em>Other (Enter Manually Below)</em></MenuItem>
                                                        </Select>
                                                    </FormControl>
                                                    <TextField label="Beneficiary Name(s)" value={item.beneficiaries} onChange={(e) => handleItemChange(item.id, "beneficiaries", e.target.value)} required fullWidth placeholder="Enter name or select above" />
                                                </BeneficiarySelectionContainer>
                                                <RadioGroupContainer component="fieldset">
                                                    <FormLabel component="legend">Has Lifelong Usufruct?</FormLabel>
                                                    <StyledRadioGroup row name={`hasLifelongUsufruct-${item.id}`} value={item.hasLifelongUsufruct.toString()} onChange={(e) => handleItemRadioChange(item.id, "hasLifelongUsufruct", e.target.value)}>
                                                        <FormControlLabel value="true" control={<Radio size="small" />} label="Yes" />
                                                        <FormControlLabel value="false" control={<Radio size="small" />} label="No" />
                                                    </StyledRadioGroup>
                                                </RadioGroupContainer>
                                                {item.hasLifelongUsufruct && (<TextField label="Usufruct Beneficiary" value={item.usufructBeneficiary} onChange={(e) => handleItemChange(item.id, "usufructBeneficiary", e.target.value)} required fullWidth />)}
                                                <TextField label="Special Conditions" value={item.specialConditions} onChange={(e) => handleItemChange(item.id, "specialConditions", e.target.value)} multiline rows={2} fullWidth />
                                            </>
                                        )}

                                        {/* === Movable Property === */}
                                        {item.type === "movable" && (
                                            <>
                                                <TextField label="Description" value={item.description} onChange={(e) => handleItemChange(item.id, "description", e.target.value)} placeholder="e.g. vehicles, furniture" required fullWidth />
                                                <TextField label="Beneficiary Name(s)" value={item.beneficiaries} onChange={(e) => handleItemChange(item.id, "beneficiaries", e.target.value)} required fullWidth />
                                            </>
                                        )}

                                        {/* === Cash Benefit === */}
                                        {item.type === "cash" && (
                                            <>
                                                <TextField label="Amount (e.g., R10000)" value={item.amount} onChange={(e) => handleItemChange(item.id, "amount", e.target.value)} required fullWidth />
                                                <TextField label="Amount in Words" value={item.amountInWords} onChange={(e) => handleItemChange(item.id, "amountInWords", e.target.value)} required fullWidth />
                                                <BeneficiarySelectionContainer>
                                                    <FormControl fullWidth>
                                                        <InputLabel id={`cash-beneficiary-select-${item.id}`}>Beneficiary</InputLabel>
                                                        <Select labelId={`cash-beneficiary-select-${item.id}`} value="" label="Beneficiary" onChange={(e) => handleBeneficiarySelect(item.id, "estateItem", e.target.value, "beneficiary")}>
                                                            <MenuItem value=""><em>-- Select Existing --</em></MenuItem>
                                                            {getPotentialBeneficiaries().map(ben => (<MenuItem key={ben.id} value={ben.id}>{ben.name} ({ben.relationship})</MenuItem>))}
                                                            <MenuItem value="other"><em>Other (Enter Manually Below)</em></MenuItem>
                                                        </Select>
                                                    </FormControl>
                                                    <TextField label="Beneficiary Name" value={item.beneficiary} onChange={(e) => handleItemChange(item.id, "beneficiary", e.target.value)} required fullWidth placeholder="Enter name or select above" />
                                                </BeneficiarySelectionContainer>
                                                <TextField label="Purpose (optional)" value={item.purpose} onChange={(e) => handleItemChange(item.id, "purpose", e.target.value)} fullWidth />
                                                <TextField label="Alternative Beneficiary (optional)" value={item.alternativeBeneficiary} onChange={(e) => handleItemChange(item.id, "alternativeBeneficiary", e.target.value)} fullWidth />
                                            </>
                                        )}

                                        {/* === Business Ownership === */}
                                        {item.type === "business" && (
                                            <>
                                                <TextField label="Company Name" value={item.companyName} onChange={(e) => handleItemChange(item.id, "companyName", e.target.value)} required fullWidth />
                                                <TextField label="Registration Number" value={item.registrationNumber} onChange={(e) => handleItemChange(item.id, "registrationNumber", e.target.value)} required fullWidth />
                                                <TextField label="Share Percentage (e.g., 48%)" value={item.sharePercentage} onChange={(e) => handleItemChange(item.id, "sharePercentage", e.target.value)} required fullWidth />
                                                <RadioGroupContainer component="fieldset">
                                                    <FormLabel component="legend">Agreement in place for distribution?</FormLabel>
                                                    <StyledRadioGroup row name={`hasAgreement-${item.id}`} value={item.hasAgreement.toString()} onChange={(e) => handleItemRadioChange(item.id, "hasAgreement", e.target.value)}>
                                                        <FormControlLabel value="true" control={<Radio size="small" />} label="Yes" />
                                                        <FormControlLabel value="false" control={<Radio size="small" />} label="No" />
                                                    </StyledRadioGroup>
                                                </RadioGroupContainer>
                                                <TextField label="Beneficiary (who inherits shares)" value={item.beneficiary} onChange={(e) => handleItemChange(item.id, "beneficiary", e.target.value)} required fullWidth />
                                            </>
                                        )}

                                        {/* === Trust === */}
                                        {item.type === "trust" && (
                                            <>
                                                <FormControl fullWidth required>
                                                    <InputLabel id={`trustType-label-${item.id}`}>Trust Type</InputLabel>
                                                    <Select labelId={`trustType-label-${item.id}`} label="Trust Type" name="trustType" value={item.trustType} onChange={(e) => handleItemChange(item.id, "trustType", e.target.value)} >
                                                        <MenuItem value=""><em>Select</em></MenuItem>
                                                        <MenuItem value="Family Trust">Family Trust</MenuItem>
                                                        <MenuItem value="Testamentary Trust">Testamentary Trust</MenuItem>
                                                        <MenuItem value="Discretionary Trust">Discretionary Trust</MenuItem>
                                                    </Select>
                                                </FormControl>
                                                <TextField label="Trust Name" value={item.trustName} onChange={(e) => handleItemChange(item.id, "trustName", e.target.value)} required fullWidth />
                                                <TextField label="Beneficiaries" value={item.beneficiaries} onChange={(e) => handleItemChange(item.id, "beneficiaries", e.target.value)} required multiline rows={2} fullWidth />
                                                <TextField label="Conditions" value={item.conditions} onChange={(e) => handleItemChange(item.id, "conditions", e.target.value)} multiline rows={2} fullWidth />
                                            </>
                                        )}

                                        {/* === Digital Asset === */}
                                        {item.type === "digital" && (
                                            <>
                                                <FormControl fullWidth required>
                                                    <InputLabel id={`assetType-label-${item.id}`}>Asset Type</InputLabel>
                                                    <Select labelId={`assetType-label-${item.id}`} label="Asset Type" name="assetType" value={item.assetType} onChange={(e) => handleItemChange(item.id, "assetType", e.target.value)}>
                                                        <MenuItem value=""><em>Select</em></MenuItem>
                                                        <MenuItem value="Cryptocurrency">Cryptocurrency</MenuItem>
                                                        <MenuItem value="Domain Names">Domain Names</MenuItem>
                                                        <MenuItem value="Digital Wallet">Digital Wallet</MenuItem>
                                                        <MenuItem value="Social Media Accounts">Social Media Accounts</MenuItem>
                                                        <MenuItem value="Other">Other</MenuItem>
                                                    </Select>
                                                </FormControl>
                                                <TextField label="Description" value={item.description} onChange={(e) => handleItemChange(item.id, "description", e.target.value)} required multiline rows={2} fullWidth />
                                                <TextField label="Access Information Hint" helperText="Where to find access details (not passwords)" value={item.accessInformation} onChange={(e) => handleItemChange(item.id, "accessInformation", e.target.value)} multiline rows={2} fullWidth />
                                                <TextField label="Beneficiary" value={item.beneficiary} onChange={(e) => handleItemChange(item.id, "beneficiary", e.target.value)} required fullWidth />
                                            </>
                                        )}

                                        {/* === Personal Bequest === */}
                                        {item.type === "personal" && (
                                            <>
                                                <TextField label="Item Description" value={item.itemDescription} onChange={(e) => handleItemChange(item.id, "itemDescription", e.target.value)} required multiline rows={2} fullWidth />
                                                <TextField label="Sentimental Value (optional)" value={item.sentimentalValue} onChange={(e) => handleItemChange(item.id, "sentimentalValue", e.target.value)} multiline rows={2} fullWidth />
                                                <TextField label="Beneficiary" value={item.beneficiary} onChange={(e) => handleItemChange(item.id, "beneficiary", e.target.value)} required fullWidth />
                                            </>
                                        )}

                                        {/* === Policy/Investment === */}
                                        {item.type === "policy" && (
                                            <>
                                                <TextField label="Policy/Investment Type" value={item.policyType} onChange={(e) => handleItemChange(item.id, "policyType", e.target.value)} required fullWidth />
                                                <TextField label="Policy Number" value={item.policyNumber} onChange={(e) => handleItemChange(item.id, "policyNumber", e.target.value)} required fullWidth />
                                                <TextField label="Provider" value={item.provider} onChange={(e) => handleItemChange(item.id, "provider", e.target.value)} required fullWidth />
                                                <TextField label="Account Number (if applicable)" value={item.accountNumber} onChange={(e) => handleItemChange(item.id, "accountNumber", e.target.value)} fullWidth />
                                            </>
                                        )}

                                        <RemoveButton
                                            variant="contained"
                                            onClick={() => removeEstateItem(item.id)}
                                            sx={{ mt: 'auto' }} // Push to bottom
                                        >
                                            Remove Item
                                        </RemoveButton>
                                    </EstateItemPaper>
                                </Grid>
                            ))}
                        </EstateItemsGrid>
                    )}
                </SectionContainer>

                <SectionContainer variant="outlined">
                    <EstateSectionHeader variant="h6">Remainder of Estate</EstateSectionHeader>
                    <Typography variant="body2" color="textSecondary" paragraph>
                        Specify how any remaining assets, not specifically bequeathed above, should be distributed. Percentages must add up to 100%.
                    </Typography>

                    {formData.remainderBeneficiaries.map((ben) => (
                        <RemainderBeneficiaryPaper key={ben.id} variant="outlined">
                            <BeneficiarySelectionContainer>
                                <FormControl fullWidth>
                                    <InputLabel id={`remainder-beneficiary-select-${ben.id}`}>Beneficiary</InputLabel>
                                    <Select labelId={`remainder-beneficiary-select-${ben.id}`} value="" label="Beneficiary" onChange={(e) => handleBeneficiarySelect(ben.id, "remainder", e.target.value)}>
                                        <MenuItem value=""><em>-- Select Existing --</em></MenuItem>
                                        {getPotentialBeneficiaries().map(potentialBen => (<MenuItem key={potentialBen.id} value={potentialBen.id}>{potentialBen.name} ({potentialBen.relationship})</MenuItem>))}
                                        <MenuItem value="other"><em>Other (Enter Manually Below)</em></MenuItem>
                                    </Select>
                                </FormControl>
                                <TextField label="Beneficiary Name" value={ben.name} onChange={(e) => handleRemainderChange(ben.id, "name", e.target.value)} required fullWidth placeholder="Enter name or select above" />
                            </BeneficiarySelectionContainer>
                            <TextField label="Relationship" value={ben.relationship} onChange={(e) => handleRemainderChange(ben.id, "relationship", e.target.value)} placeholder="e.g. spouse, child, friend" required fullWidth />
                            <TextField
                                label="Percentage"
                                type="number"
                                value={ben.percentage}
                                onChange={(e) => handleRemainderChange(ben.id, "percentage", e.target.value)}
                                required
                                fullWidth
                                inputProps={{ min: "1", max: "100", step: "1" }}
                            />
                            <RemoveButton
                                variant="contained"
                                onClick={() => removeRemainderBeneficiary(ben.id)}
                                size="small"
                            >
                                Remove
                            </RemoveButton>
                        </RemainderBeneficiaryPaper>
                    ))}

                    <AddButton
                        variant="contained"
                        onClick={addRemainderBeneficiary}
                    >
                        Add Remainder Beneficiary
                    </AddButton>

                    <PercentageHelperBox error={percentageError}>
                        <Typography component="p">
                            Total percentage: {totalRemainderPercentage}% (must equal 100%)
                        </Typography>
                    </PercentageHelperBox>
                </SectionContainer>

                <ButtonGroupContainer>
                    <BackButton
                        type="button"
                        variant="contained"
                        onClick={handleBack}
                        disabled={isSubmitting}
                    >
                        Back
                    </BackButton>
                    <SubmitButton
                        type="submit"
                        variant="contained"
                        color="primary"
                        disabled={isSubmitting || percentageError} // Also disable if percentages don't match
                        startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : null}
                        onClick={(e) => { handleSubmit(e) }}
                    >
                        {isSubmitting ? 'Processing...' : 'Generate Will'}
                    </SubmitButton>
                </ButtonGroupContainer>
            </StyledForm>
        </FormPageContainer>
    );
} 